/*
    View for displaying a selectable list of devices, with the logs for the selected device

    Usage:
        var Backbone        = require('backbone');
    var Radio = require('backbone.radio');
        var DevicesPaneView = require('@/views/device_pane_layout');
        var apiCh = Radio.channel('api');

        var devices_collection = apiCh.request('device');
        devices_collection.fetch_by_devicegroup(devicegroup);

        var devices_pane_view = new DevicesPaneView({
                collection: devices_collection,
                devicegroup: devicegroup
            });
    Arguments:
        devicegroup: Instance of v5 devicegroup model 'models/v5/devicegroup'
        collection: Instance of v5 devices collection 'collection/v5/devices'
            See above syntax.
*/

import _ from 'underscore';
import Marionette from 'backbone.marionette';
import Radio from 'backbone.radio';
import Handlebars from 'handlebars';
import DeviceItemView from '@/views/device_item_view';
import DeviceLogInfoView from '@/views/device_log_info';
import template from '@/../templates/device-pane-layout.hbs';
import empty_log_template from '@/../templates/empty-device-pane.hbs';

var globalCh = Radio.channel('global');
var apiCh = Radio.channel('api');
var ga = globalCh.request('app_options', 'ga_account');

function devices_filter(devicegroup, device) {
    if (devicegroup.type === 'pre_factoryfixture_devicegroup') {
        return device.rel.get('devicegroup') === devicegroup 
        || device.rel.get('devicegroup') === devicegroup.rel.get('production_target');
    }
    var dev_devicegroup = device.rel.get('devicegroup');
    return dev_devicegroup === devicegroup;
}

var layout = Marionette.View.extend({
    template: Handlebars.compile(template),
    templateContext: function(){
        var devices_type = _.has(this.options, 'devices_type') ? this.options.devices_type : 'Devices';
        let hide_assign_button = false;
        if (this.options.devicegroup.type === 'pre_production_devicegroup') hide_assign_button = true;
        return {
            devices_type: devices_type,
            hide_assign_button: hide_assign_button
        };
    },
    className: 'devices-pane',
    initialize: function(options) {
        this.mergeOptions(options, ['devicegroup']);
        this.devices_filter = _.partial(devices_filter, this.options.devicegroup);
    },
    regions: {
        devices: '.devices-region',
        device_log_info: '.device-log-info'
    },
    ui: {
        assign_btn: '.assign',
        sidebar: '.sidebar',
        toggle_devices_list: '.toggle-devices-list',
        toggle_devices_list_icon: '.toggle-devices-list > .fa',
        num_devices: '.num-devices',
        clear_log_button: '.controls .clear',
        pause_button: '.controls .pause',
        lock_icon: '.controls .lock',
        expand_button: '.controls .expand',
        collapse_button: '.controls .collapse'
    },
    events: {
        'click @ui.assign_btn': 'show_assign_devices_dialog',
        'click @ui.toggle_devices_list': 'toggle_devices_list',
        'click @ui.clear_log_button': 'onclick_clear_log_button',
        'click @ui.pause_button': 'onclick_pause_button'
    },
    initialize_devices_list: function() {
        var self = this;
        apiCh.on('remote', function(event) {
            if ( event && event.action == 'delete' ) {
                if ( self.collection.contains(event.model) ) {
                    self.collection.remove(event.model);
                }
            }
        });
        var devices_list_view = new Marionette.CollectionView({
            collection: this.collection,
            devicegroup: this.devicegroup,
            filter: this.devices_filter,
            device: this.active_device,
            className: 'sidebar-ish',
            tagName: 'ul',
            childView: this.getOption('childView') || DeviceItemView,
            childViewOptions: this.getOption('childViewOptions') || {
                className: 'device',
                tagName: 'li'
            },
            childViewEvents: {
                'show:device': _.bind(function(device) {
                    this.show_device(device);
                }, this)
            }
        });

        this.showChildView('devices', devices_list_view);

        this.ui.toggle_devices_list.tooltip({
            delay: { show: 1000 }
        });

        (this.collection.length > 0)
            ? this.show_devices_list()
            : this.hide_devices_list();

        this.set_num_devices_from_view(devices_list_view);

        this.listenTo(this.collection, 'change update', function() {
            this.set_num_devices_from_view(devices_list_view);
        });

    },
    toggle_devices_list: function() {
        if (ga) {
            window.ga('send', 'event', 'devicesLogsPane', 'Toggle Devices List');
        }
        this.devices_list_visible
            ? this.hide_devices_list.call(this)
            : this.show_devices_list.call(this);
    },
    show_devices_list: function() {
        this.ui.sidebar.show();

        // Update handle style
        this.ui.toggle_devices_list_icon.removeClass('fa-caret-up');
        this.ui.toggle_devices_list_icon.addClass('fa-caret-down');

        this.ui.toggle_devices_list
            .attr('title', 'Hide devices list')
            .tooltip('fixTitle');

        this.devices_list_visible = true;
    },
    hide_devices_list: function() {
        this.ui.sidebar.hide();

        // Update handle style
        this.ui.toggle_devices_list_icon.removeClass('fa-caret-down');
        this.ui.toggle_devices_list_icon.addClass('fa-caret-up');

        this.ui.toggle_devices_list
            .attr('title', 'Show devices list')
            .tooltip('fixTitle');

        this.devices_list_visible = false;
    },

    show_device: function(device) {
        if (this.active_device && this.active_device === device) {
            return;
        }

        var available_devices = this.collection.filter(this.devices_filter);
        var devicegroup = this.options.devicegroup;
        var product = devicegroup.rel.get('product');
        if (!_.contains(available_devices, device) || device === undefined) {
            device = available_devices[0];
        }
        if (!device) {
            this.show_empty_view();
            if (product === undefined) {
                product = this.getOption('pre_factoryfixture_devicegroup').rel.get('product');
            }
            return;
        }
        this.active_device = device;
        this.triggerMethod('state:active:device', this.active_device);
        globalCh.request('set_selection', {
            type: 'v5_device',
            id: device.id
        });
        this.showChildView('device_log_info', new DeviceLogInfoView({
            model: device
        }));
    },
    show_empty_view: function() {
        var empty_log_view;
        if (this.devicegroup.type === 'pre_production_devicegroup'){
            empty_log_view = new Marionette.View({
                template: Handlebars.compile('<p><i class="fa fa-exclamation-triangle" aria-hidden="true"></i> No Test Production Devices</p>'),
                className: 'text-center no-devices'
            });
        } else if (this.devicegroup.type === 'pre_dut_devicegroup') {
            empty_log_view = new Marionette.View({
                template: Handlebars.compile('<p><i class="fa fa-exclamation-triangle" aria-hidden="true"></i> No Test DUTs</p>'),
                className: 'text-center no-devices'
            });
        } else {
            empty_log_view = new Marionette.View({
                template: Handlebars.compile(empty_log_template),
                className: 'text-center no-devices'
            });
        }

        this.showChildView('device_log_info', empty_log_view);
        this.ui.sidebar.hide();
    },
    show_assign_devices_dialog: function() {
        var devicegroup = this.devicegroup;
        if (ga) {
            window.ga('send', 'event', 'devicesLogsPane', 'Show Assign Devices Dialog', devicegroup.type);
        }
        globalCh.request('show_assign_devices_dialog', devicegroup);
    },
    onRender: function() {
        var self = this;
        this.ui.clear_log_button.tooltip();
        this.ui.pause_button.tooltip();
        this.ui.lock_icon.tooltip();
        
        this.initialize_devices_list();
        this.show_device(this.options.device);

        if (this.devicegroup.type === 'pre_dut_devicegroup') {
            this.ui.assign_btn.hide();
        }
        this.listenTo(this.collection, 'change', function(device){
            if (device.rel.get('devicegroup') 
                && device.rel.get('devicegroup').id !== this.devicegroup.id
                && this.devicegroup.rel.get('production_target')
                && device.rel.get('devicegroup').id !== this.devicegroup.rel.get('production_target').get('id')){
                    this.collection.remove(device);
            }
            if (device === this.active_device ) this.show_device(this.active_device);
        });
        this.listenTo(this.collection, 'add', function(device){
            if ( self.active_device && self.active_device.id === device.id ) return;
            self.show_devices_list();
            if ( !self.active_device ) self.show_device(device);
        });
        this.listenTo(this.collection, 'remove', function(device){
            if ( self.active_device && self.active_device.id === device.id ) {
                self.active_device = undefined;
            }
            this.show_devices_list();
            this.show_device();
        });
    },
    log_locked: function() {
        if (ga) {
            window.ga('send', 'event', 'devicesLogsPane', 'Log Locked');
        }
        this.ui.lock_icon.addClass('active');
    },
    log_unlocked: function() {
        if (ga) {
            window.ga('send', 'event', 'devicesLogsPane', 'Log Unlocked');
        }
        this.ui.lock_icon.removeClass('active');
    },
    set_num_devices_from_view: function(view) {
        this.ui.num_devices.text(view.children && view.children.length);
    },
    handle_syntax_check_results: function(results) {
        var device_log_info = this.getRegion('device_log_info').currentView;
        if (!device_log_info || !_.isFunction(device_log_info.handle_syntax_check_results) ) return;
        device_log_info.handle_syntax_check_results(results);
    }
});

export default layout;

